import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { sendWithEncryptedPasswords } from '@/lib/credentialEncryption';

export interface Customer {
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatarUrl?: string;
  organizationId?: string;
  organizationName?: string;
  timezone?: string;
  locale?: string;
  tenantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Login step 1 returns this instead of tokens: mandatory email OTP.
export type LoginResult =
  | { status: 'mfa_required' }
  | { status: 'error' };

export type OtpResult =
  | { status: 'ok' }
  | { status: 'error'; code?: string; message?: string };

const TENANT_HEADER = '11111111-1111-1111-1111-111111111111';

interface AuthState {
  customer: Customer | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;

  // Pending email-OTP challenge (between login step 1 and step 2).
  mfaToken: string | null;
  maskedEmail: string | null;
  otpExpiresIn: number | null;

  login: (email: string, password: string) => Promise<LoginResult>;
  verifyOtp: (code: string) => Promise<OtpResult>;
  resendOtp: () => Promise<OtpResult>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  setCustomer: (customer: Customer, token: string) => void;
  setTokens: (token: string, refreshToken: string) => void;
  fetchProfile: () => Promise<void>;
  clearError: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      customer: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,
      error: null,
      mfaToken: null,
      maskedEmail: null,
      otpExpiresIn: null,

      setHydrated: () => {
        set({ isHydrated: true });
      },

      // Step 1: password. On success the server emails a code and returns an
      // OTP challenge — NO tokens yet. We stash the mfaToken for step 2.
      login: async (email: string, password: string): Promise<LoginResult> => {
        set({ isLoading: true, error: null });
        try {
          const response = await sendWithEncryptedPasswords({ email, password }, (body) =>
            fetch('/api/v1/portal/auth/login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-tenant-id': TENANT_HEADER,
              },
              body: JSON.stringify(body),
            }),
          );

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.message || 'Login failed');
          }

          set({
            mfaToken: data.mfaToken,
            maskedEmail: data.maskedEmail,
            otpExpiresIn: data.expiresInSeconds ?? 600,
            isLoading: false,
          });
          return { status: 'mfa_required' };
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          return { status: 'error' };
        }
      },

      // Step 2: verify the emailed code and complete login.
      verifyOtp: async (code: string): Promise<OtpResult> => {
        const { mfaToken } = get();
        if (!mfaToken) {
          return { status: 'error', code: 'MFA_TOKEN_INVALID', message: 'Please sign in again.' };
        }
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/v1/portal/auth/mfa/verify-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-tenant-id': TENANT_HEADER },
            body: JSON.stringify({ mfaToken, code }),
          });
          const data = await response.json();
          if (!response.ok) {
            set({ isLoading: false });
            return { status: 'error', code: data.code, message: data.message };
          }
          set({
            customer: data.customer,
            token: data.token,
            refreshToken: data.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            mfaToken: null,
            maskedEmail: null,
            otpExpiresIn: null,
          });
          return { status: 'ok' };
        } catch (error) {
          set({ isLoading: false });
          return { status: 'error', message: error instanceof Error ? error.message : 'Verification failed' };
        }
      },

      // Request a fresh code (after the countdown lapses).
      resendOtp: async (): Promise<OtpResult> => {
        const { mfaToken } = get();
        if (!mfaToken) {
          return { status: 'error', code: 'MFA_TOKEN_INVALID', message: 'Please sign in again.' };
        }
        try {
          const response = await fetch('/api/v1/portal/auth/mfa/resend', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-tenant-id': TENANT_HEADER },
            body: JSON.stringify({ mfaToken }),
          });
          const data = await response.json();
          if (!response.ok) {
            return { status: 'error', code: data.code, message: data.message };
          }
          set({
            mfaToken: data.mfaToken ?? mfaToken,
            maskedEmail: data.maskedEmail ?? get().maskedEmail,
            otpExpiresIn: data.expiresInSeconds ?? 600,
          });
          return { status: 'ok' };
        } catch (error) {
          return { status: 'error', message: error instanceof Error ? error.message : 'Could not resend code' };
        }
      },

      register: async (name: string, email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await sendWithEncryptedPasswords({ name, email, password }, (body) =>
            fetch('/api/v1/portal/auth/register', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-tenant-id': '11111111-1111-1111-1111-111111111111',
              },
              body: JSON.stringify(body),
            }),
          );

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Registration failed');
          }

          const data = await response.json();
          set({
            customer: data.customer,
            token: data.token,
            refreshToken: data.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
          return false;
        }
      },

      logout: () => {
        set({
          customer: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          mfaToken: null,
          maskedEmail: null,
          otpExpiresIn: null,
        });
      },

      setCustomer: (customer: Customer, token: string) => {
        set({
          customer,
          token,
          isAuthenticated: true,
        });
      },

      setTokens: (token: string, refreshToken: string) => {
        set({ token, refreshToken });
      },

      fetchProfile: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetch('/api/v1/portal/profile', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-tenant-id': '11111111-1111-1111-1111-111111111111',
            },
          });

          if (!response.ok) return;

          const profile = await response.json();
          set({ customer: profile });
        } catch {
          // Profile fetch is non-critical — silently ignore failures
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'customer-portal-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        customer: state.customer,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    }
  )
);
